/**
 * Credit held back by an order minimum.
 *
 * A granted credit can carry a minimum: "10,00 € off orders of 40,00 € or
 * more". `GET /credit/balance` does not apply minimums to `balanceCents` (a
 * balance is not an order), so on a 31,99 € cart a customer with 5,04 € of
 * plain credit and that 10,00 € was told "Ostane še 10,00 €" after the 5,04 €
 * went on — and concluded their two credits do not combine.
 *
 * With an order id the server now also answers `lockedByMinimum` (the part of
 * the balance this order cannot use ONLY because of a minimum) and
 * `orderGoodsCents` (the goods total it judged that on: pre-credit, shipping
 * excluded). This module turns those into what the box says. It does not
 * re-derive eligibility: the server applies every other rule first.
 */

export type LockedByMinimum = {
  amountCents: number;
  minimumOrderCents: number;
  currency: string;
};

export type CreditMinimumLine = {
  amountCents: number;
  minimumOrderCents: number;
  /** Goods still missing to reach the minimum; null when unknown. */
  missingCents: number | null;
};

/**
 * What THIS order can spend: the balance minus whatever a minimum holds back.
 * Unknown stays unknown (null), and an older server that sends no
 * `lockedByMinimum` leaves the balance as it was.
 */
export function usableNowCents(
  balanceCents: number | null,
  locked: readonly LockedByMinimum[] | null | undefined,
): number | null {
  if (balanceCents === null) return null;
  const held = (locked ?? []).reduce((acc, l) => acc + Math.max(0, l.amountCents), 0);
  return Math.max(0, balanceCents - held);
}

/**
 * One line per minimum, in the server's order (lowest minimum first). The
 * missing amount is measured on the server's own goods total — the checkout's
 * totals include shipping, which a minimum never counts — and left out when
 * the server did not send it.
 */
export function creditMinimumLines(
  locked: readonly LockedByMinimum[] | null | undefined,
  orderGoodsCents: number | null | undefined,
): CreditMinimumLine[] {
  const goods = typeof orderGoodsCents === "number" ? orderGoodsCents : null;
  return (locked ?? [])
    .filter((l) => l.amountCents > 0)
    .map((l) => ({
      amountCents: l.amountCents,
      minimumOrderCents: l.minimumOrderCents,
      missingCents: goods === null ? null : Math.max(0, l.minimumOrderCents - goods),
    }));
}

/** The sentence for one line, in the house `t(key, slovenianFallback, params)` form. */
export function creditMinimumMessage(
  line: CreditMinimumLine,
  money: (cents: number) => string,
): { key: string; fallback: string; params: Record<string, string> } {
  const params: Record<string, string> = {
    amount: money(line.amountCents),
    minimum: money(line.minimumOrderCents),
  };
  if (line.missingCents !== null && line.missingCents > 0) {
    return {
      key: "credit.lockedByMinimumMissing",
      fallback:
        "{{amount}} dobroimetja lahko porabite pri naročilu od {{minimum}} naprej (poštnina se ne šteje). Dodajte še za {{missing}} in se odšteje.",
      params: { ...params, missing: money(line.missingCents) },
    };
  }
  return {
    key: "credit.lockedByMinimum",
    fallback:
      "{{amount}} dobroimetja lahko porabite pri naročilu od {{minimum}} naprej (poštnina se ne šteje).",
    params,
  };
}

/**
 * The account page's part of the balance that comes with a minimum, from
 * GET /credit/mine's lots: summed per currency and minimum, lowest first.
 * Lots without a minimum (0) are the unconditioned rest and are not listed.
 */
export function accountMinimumLines(
  lots:
    | ReadonlyArray<{ currency: string; remainingCents: number; minimumOrderCents: number }>
    | null
    | undefined,
): Array<{ currency: string; amountCents: number; minimumOrderCents: number }> {
  const sums = new Map<string, { currency: string; amountCents: number; minimumOrderCents: number }>();
  for (const lot of lots ?? []) {
    if (!(lot.minimumOrderCents > 0) || !(lot.remainingCents > 0)) continue;
    const key = `${lot.currency}\u0000${lot.minimumOrderCents}`;
    const line = sums.get(key) ?? {
      currency: lot.currency,
      amountCents: 0,
      minimumOrderCents: lot.minimumOrderCents,
    };
    line.amountCents += lot.remainingCents;
    sums.set(key, line);
  }
  return [...sums.values()].sort(
    (a, b) =>
      a.currency.localeCompare(b.currency) || a.minimumOrderCents - b.minimumOrderCents,
  );
}

/**
 * Where the checkout box puts held-back credit.
 *
 *  - `showLines`: only on a cart that can still change — adding prints is
 *    the whole point of the sentence, and a placed order cannot grow;
 *  - `showRemaining`: "Ostane še 0,00 €" above "10,00 € … from 40,00 €"
 *    reads as a contradiction, so a zero "left" is dropped when the lines
 *    already say where the rest of the credit is. Any other case keeps it.
 */
export function creditMinimumLayout(input: {
  orderEditable: boolean;
  lines: readonly CreditMinimumLine[];
  remainingCents: number | null;
}): { showLines: boolean; showRemaining: boolean } {
  const showLines = input.orderEditable && input.lines.length > 0;
  return {
    showLines,
    showRemaining: !(showLines && input.remainingCents === 0),
  };
}
